import { cn } from '@/lib/cn';

export interface InputProps extends React.ComponentProps<'input'> {
  prefixElement?: React.ReactNode;
  suffixElement?: React.ReactNode;
  wrapperClassName?: string;
  // Turns the field into a floating label: the label starts centred at full
  // size where a placeholder would normally be, then shrinks and rises to the
  // top once the field is focused or holds a value. Used by the auth screens;
  // leaving this undefined keeps the original markup untouched everywhere else.
  label?: React.ReactNode;
  labelClassName?: string;
}

// The float is driven by `:placeholder-shown`, so the input always carries a
// single-space placeholder — that is what makes "empty" a CSS-observable state
// without tracking the value in React. A caller's own placeholder would defeat
// the selector (it would never be "shown" as empty), so it is ignored here: a
// floating label *is* the placeholder.
// Geometry, for a 56px (h-14) box. Resting, the label is centred. Floated, the
// label sits 10px from the top and the value 10px from the bottom, which leaves
// the pair optically centred rather than crowded against the top edge:
//   label  10..22  (text-xs, leading-none -> 12px tall)
//   value  26..46  (text-sm,  h-5         -> 20px tall)
// The resting label's top is 28 - 14/2 = 21, so -18px lands it at 10.
// Chrome's page-load autofill paints a value but leaves the field
// `:placeholder-shown` (and `.value` empty to JS) until the user interacts, so
// `:autofill` floats the label too.
const FLOAT_TRIGGERS =
  'peer-focus:-translate-y-[18px] peer-focus:text-xs peer-[:not(:placeholder-shown)]:-translate-y-[18px] peer-[:not(:placeholder-shown)]:text-xs peer-autofill:-translate-y-[18px] peer-autofill:text-xs';

function Input({
  type,
  className,
  suffixElement,
  prefixElement,
  disabled,
  wrapperClassName,
  label,
  labelClassName,
  placeholder,
  ...props
}: InputProps) {
  const isFloating = Boolean(label);
  // Dividers are drawn on our own parts, not with `divide-x`: that rule borders
  // every child but the last, so the icon a password manager appends to the
  // wrapper turned the field and suffix into bordered middles. `border-inherit`
  // follows the wrapper's colour, focus included. Floating (auth) fields have
  // no divider: a login box stays one plain box, so a password manager can
  // place its icon just left of the suffix (show-password eye).
  const dividerBeforeSuffix =
    !isFloating && Boolean(suffixElement) && 'border-e border-inherit';

  const field = (
    <input
      type={type}
      data-slot="input"
      placeholder={isFloating ? ' ' : placeholder}
      className={cn(
        'placeholder:text-text-placeholder disabled:placeholder-text-disabled w-full outline-none disabled:cursor-not-allowed',
        isFloating &&
          'peer absolute inset-x-0 bottom-[10px] h-5 bg-transparent',
        // A no-op animation whose `animationstart` bubbles up, so a form can
        // notice an autofill that fires no input event (see `useAutofillSync`).
        isFloating && 'autofill:animate-[autofill-detect_1ms]',
        dividerBeforeSuffix,
        className
      )}
      disabled={disabled}
      {...props}
    />
  );

  return (
    <div
      className={cn(
        'border-border-color flex w-full items-center gap-2 rounded-md border bg-white px-3 py-2 text-sm transition-colors duration-200',
        'focus-within:border-neutral-50',
        isFloating && 'h-14 py-0',
        disabled && 'cursor-not-allowed bg-slate-50',
        'has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-slate-50',
        wrapperClassName
      )}
    >
      {Boolean(prefixElement) && (
        <span
          className={cn(
            'contents',
            !isFloating && 'border-inherit [&>*]:border-e [&>*]:border-inherit'
          )}
        >
          {prefixElement}
        </span>
      )}
      {isFloating ? (
        <div className="relative h-full w-full">
          {field}
          <label
            htmlFor={props.id}
            className={cn(
              // Resting position: centred in the box at body size, standing in
              // for the placeholder. `origin-left` keeps the shrink anchored to
              // the left edge so it rises straight up rather than drifting in.
              'text-text-muted absolute inset-x-0 top-1/2 origin-left -translate-y-1/2 cursor-text text-sm leading-none transition-all duration-200 ease-out',
              FLOAT_TRIGGERS,
              disabled && 'cursor-not-allowed',
              labelClassName
            )}
          >
            {label}
          </label>
        </div>
      ) : (
        field
      )}
      {suffixElement ?? null}
    </div>
  );
}

export { Input };
